import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLang } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { RankBadge } from '@/utils/ranks';
import ScrollReveal from '@/components/effects/ScrollReveal';
import { Calendar, MessageCircle, Pen, Settings } from 'lucide-react';

export default function Profile() {
  const { username } = useParams();
  const { t } = useLang();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/profiles/${username}`).then(r => { setProfile(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [username]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-sacred-gold animate-gold-pulse">{t('common.loading')}</div></div>;
  if (!profile) return <div className="min-h-screen flex items-center justify-center"><p className="text-text-muted">{t('common.notFound')}</p></div>;

  const isOwn = currentUser?.username === username;
  const avatarUrl = profile.avatar_url ? (profile.avatar_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${profile.avatar_url}` : profile.avatar_url) : null;

  return (
    <div data-testid="profile-page" className="min-h-screen py-20 md:py-32 px-4">
      <div className="max-w-3xl mx-auto">
        <ScrollReveal>
          <div className="glass-card rounded-2xl p-8 md:p-10 text-center" data-testid="profile-card">
            <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden gold-glow">
              {avatarUrl ? (
                <img src={avatarUrl} alt={profile.display_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-sacred-gold/10 flex items-center justify-center text-sacred-gold text-3xl font-display">
                  {profile.display_name?.[0]?.toUpperCase() || '?'}
                </div>
              )}
            </div>
            <h1 className="text-3xl font-display text-heading mb-1" data-testid="profile-name">{profile.display_name}</h1>
            <p className="text-text-muted/70 font-body text-sm mb-3">@{profile.username}</p>
            <div className="mb-4">
              <RankBadge rank={profile.rank || 'Seeker'} size="md" />
            </div>
            {profile.bio && <p className="text-warm-brown font-body max-w-md mx-auto mb-6">{profile.bio}</p>}

            <div className="flex justify-center gap-8 mb-6">
              <div className="text-center">
                <p className="text-xl font-display text-deep-brown">{profile.post_count || 0}</p>
                <p className="text-text-muted/70 text-xs font-body">{t('profile.posts')}</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-display text-deep-brown">{profile.comment_count || 0}</p>
                <p className="text-text-muted/70 text-xs font-body">{t('profile.comments')}</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-body text-text-muted/70 flex items-center gap-1">
                  <Calendar size={12} /> {t('profile.joined')} {profile.created_at ? new Date(profile.created_at).toLocaleDateString() : ''}
                </p>
              </div>
            </div>

            {isOwn && (
              <Link to="/settings" className="glass-button px-6 py-2 rounded-full font-body text-sm inline-flex items-center gap-2" data-testid="edit-profile-btn">
                <Settings size={14} /> {t('profile.editProfile')}
              </Link>
            )}
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
}
